import React, { useState, useEffect } from "react";
import { App } from "antd";
import { useAuth } from "../../hooks/useAuth";
import PermissionWrapper, {
  AdminOnly,
  PermissionButton,
} from "../common/PermissionWrapper";
import { PERMISSIONS } from "../../utils/permissions";
import apiService from "../../services/api";
import { formatDateTime } from "../../utils/formatters";
import "./UserManagement.css";
import "../common/PermissionWrapper.css";
import {
  Plus,
  Search,
  User,
  Shield,
  ShieldCheck,
  ShieldX,
  Edit2,
  Trash2,
  Power,
  X,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  Clock,
  Mail,
} from "lucide-react";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const { modal } = App.useApp();
  const { user: currentLoggedUser, hasPermission } = useAuth();

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    role: "user",
    status: "active",
  });

  useEffect(() => {
    loadUsers();
  }, [currentPage, searchTerm, roleFilter, statusFilter]);

  const loadUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        page: currentPage,
        page_size: pageSize,
        ...(searchTerm && { search: searchTerm }),
        ...(roleFilter && { role: roleFilter }),
        ...(statusFilter && { status: statusFilter }),
      };

      const response = await apiService.users.getUsers(params);
      // Handle response format: {success: true, data: {list: [...], total: N}, message: "..."}
      if (!response.data.success) {
        throw new Error(response.data.message || "获取用户列表失败");
      }
      const users = response.data.data?.list || [];
      const total = response.data.data?.total || 0;
      setUsers(users);
      setTotal(total);
    } catch (err) {
      setError(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        "加载用户列表失败",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      await apiService.users.createUser(formData);
      setSuccess("用户创建成功");
      setShowCreateModal(false);
      resetForm();
      loadUsers();
    } catch (err) {
      setError(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        "创建用户失败",
      );
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }

      await apiService.users.updateUser(currentUser.id, updateData);
      setSuccess("用户更新成功");
      setShowEditModal(false);
      resetForm();
      loadUsers();
    } catch (err) {
      setError(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        "更新用户失败",
      );
    }
  };

  const handleDeleteUser = async (userId, username) => {
    modal.confirm({
      title: "删除用户",
      content: `确定要删除用户 "${username}" 吗？此操作不可撤销。`,
      okText: "确定",
      cancelText: "取消",
      okType: "danger",
      onOk: async () => {
        setError("");
        setSuccess("");

        try {
          await apiService.users.deleteUser(userId);
          setSuccess("用户删除成功");
          loadUsers();
        } catch (err) {
          setError(
            err.response?.data?.error?.message ||
            err.response?.data?.message ||
            err.message ||
            "删除用户失败",
          );
        }
      },
    });
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    const action = newStatus === "active" ? "启用" : "禁用";

    modal.confirm({
      title: `${action}用户`,
      content: `确定要${action}该用户吗？`,
      okText: "确定",
      cancelText: "取消",
      okType: newStatus === "active" ? "primary" : "danger",
      onOk: async () => {
        setError("");
        setSuccess("");

        try {
          await apiService.users.updateUserStatus(userId, newStatus);
          setSuccess(`用户${action}成功`);
          loadUsers();
        } catch (err) {
          setError(
            err.response?.data?.error?.message ||
            err.response?.data?.message ||
            err.message ||
            `${action}用户失败`,
          );
        }
      },
    });
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (user) => {
    setCurrentUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: "",
      role: user.role,
      status: user.status,
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      username: "",
      email: "",
      password: "",
      role: "user",
      status: "active",
    });
    setCurrentUser(null);
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };


  const isCurrentUser = (userId) => {
    return currentLoggedUser?.id === userId;
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="user-management animate-fade-in">
      <div className="page-header">
        <div className="header-title">
          <User className="header-icon" />
          <h1>用户管理</h1>
        </div>
        <PermissionButton
          permission={PERMISSIONS.CREATE_USER}
          className="btn btn-primary"
          onClick={openCreateModal}
        >
          <Plus className="btn-icon" />
          创建用户
        </PermissionButton>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* 搜索和筛选 */}
      <div className="filters">
        <div className="search-box">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder="搜索用户名或邮箱..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">所有角色</option>
          <option value="admin">管理员</option>
          <option value="user">普通用户</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">所有状态</option>
          <option value="active">启用</option>
          <option value="inactive">禁用</option>
        </select>
      </div>

      {/* 用户列表 */}
      <div className="table-container">
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <span>加载中...</span>
          </div>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>
                  <UserCheck className="th-icon" />
                  用户名
                </th>
                <th>
                  <Mail className="th-icon" />
                  邮箱
                </th>
                <th>
                  <Shield className="th-icon" />
                  角色
                </th>
                <th>
                  <Power className="th-icon" />
                  状态
                </th>
                <th>
                  <Clock className="th-icon" />
                  创建时间
                </th>
                <th>
                  <Clock className="th-icon" />
                  最后登录
                </th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.id}
                  className={isCurrentUser(user.id) ? "current-user" : ""}
                >
                  <td>{user.id}</td>
                  <td>
                    <div className="username-cell">
                      <span className="username">{user.username}</span>
                      {isCurrentUser(user.id) && (
                        <span className="current-tag">
                          <UserCheck className="tag-icon" />
                          当前
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`role-badge ${user.role}`}>
                      {user.role === "admin" ? (
                        <>
                          <Shield className="badge-icon" />
                          管理员
                        </>
                      ) : (
                        <>
                          <User className="badge-icon" />
                          普通用户
                        </>
                      )}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.status}`}>
                      {user.status === "active" ? (
                        <>
                          <ShieldCheck className="badge-icon" />
                          启用
                        </>
                      ) : (
                        <>
                          <ShieldX className="badge-icon" />
                          禁用
                        </>
                      )}
                    </span>
                  </td>
                  <td>{formatDateTime(user.created_at)}</td>
                  <td>
                    {user.last_login_at
                      ? formatDateTime(user.last_login_at)
                      : "从未登录"}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <PermissionButton
                        permission={PERMISSIONS.UPDATE_USER}
                        className="btn btn-sm btn-secondary btn-icon-only"
                        onClick={() => openEditModal(user)}
                        title="编辑"
                      >
                        <Edit2 className="btn-icon" />
                      </PermissionButton>
                      {!isCurrentUser(user.id) && (
                        <>
                          <PermissionButton
                            permission={PERMISSIONS.MANAGE_USER_STATUS}
                            className={`btn btn-sm ${user.status === "active" ? "btn-warning" : "btn-success"} btn-icon-only`}
                            onClick={() =>
                              handleToggleStatus(user.id, user.status)
                            }
                            title={user.status === "active" ? "禁用" : "启用"}
                          >
                            <Power className="btn-icon" />
                          </PermissionButton>
                          <PermissionButton
                            permission={PERMISSIONS.DELETE_USER}
                            className="btn btn-sm btn-danger btn-icon-only"
                            onClick={() =>
                              handleDeleteUser(user.id, user.username)
                            }
                            title="删除"
                          >
                            <Trash2 className="btn-icon" />
                          </PermissionButton>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 分页 */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-sm"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            <ChevronLeft className="btn-icon" />
            上一页
          </button>
          <span className="page-info">
            第 {currentPage} 页，共 {totalPages} 页，总计 {total} 条记录
          </span>
          <button
            className="btn btn-sm"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            下一页
            <ChevronRight className="btn-icon" />
          </button>
        </div>
      )}

      {/* 创建用户模态框 */}
      {showCreateModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="modal animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="modal-title">
                <UserCheck className="modal-icon" />
                <h2>创建用户</h2>
              </div>
              <button
                className="close-btn"
                onClick={() => setShowCreateModal(false)}
              >
                <X />
              </button>
            </div>
            <form onSubmit={handleCreateUser} className="modal-body">
              <div className="form-group">
                <label>用户名 *</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleFormChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>邮箱 *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>密码 *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleFormChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>角色</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleFormChange}
                  className="form-select"
                >
                  <option value="user">普通用户</option>
                  <option value="admin">管理员</option>
                </select>
              </div>
              <div className="form-group">
                <label>状态</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleFormChange}
                  className="form-select"
                >
                  <option value="active">启用</option>
                  <option value="inactive">禁用</option>
                </select>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  创建
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 编辑用户模态框 */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div
            className="modal animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="modal-title">
                <Edit2 className="modal-icon" />
                <h2>编辑用户</h2>
              </div>
              <button
                className="close-btn"
                onClick={() => setShowEditModal(false)}
              >
                <X />
              </button>
            </div>
            <form onSubmit={handleUpdateUser} className="modal-body">
              <div className="form-group">
                <label>用户名 *</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleFormChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>邮箱 *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>新密码 (留空表示不修改)</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleFormChange}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>角色</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleFormChange}
                  className="form-select"
                  disabled={isCurrentUser(currentUser?.id)}
                >
                  <option value="user">普通用户</option>
                  <option value="admin">管理员</option>
                </select>
                {isCurrentUser(currentUser?.id) && (
                  <small className="form-help">无法修改自己的角色</small>
                )}
              </div>
              <div className="form-group">
                <label>状态</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleFormChange}
                  className="form-select"
                  disabled={isCurrentUser(currentUser?.id)}
                >
                  <option value="active">启用</option>
                  <option value="inactive">禁用</option>
                </select>
                {isCurrentUser(currentUser?.id) && (
                  <small className="form-help">无法修改自己的状态</small>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  保存
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
